import React, { useEffect } from 'react'

import {
  Box,
  Card,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { useForm } from 'react-hook-form'

import { LoadingFeedback } from 'components'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import { routes } from 'Routes'
import { useHistory } from 'react-router-dom'

import useStyles from './styles'

import courseConstants from 'constants/courseAccess'

import schema, { AUTO_ENROLL_MODES } from './schema'

import { AccessTypeCard, CronNotificationsCard } from './components'

const StepSettings = ({
  courseId,
  courseCompanyConfig,
  isCourseCompanyConfigLoading,
  formRef,
  isEdit,
}) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const history = useHistory()

  const { ACCESS_TYPES, LOGIN_MODES } = courseConstants

  const { handleSubmit, watch, reset, setValue, register, errors } = useForm({
    validationSchema: schema,
    defaultValues: {
      showAnswerKey: false,
      autoEnrollMode: AUTO_ENROLL_MODES.NONE,
      autoEnrollProfileIds: [],
      notifyOnCompleteEnabled: false,
      notifyOnCompleteDate: '',
      accessType: ACCESS_TYPES.PRIVATE_ACCESS,
      loginMode: LOGIN_MODES.TWO_FACTOR_EMAIL,
    },
  })

  useEffect(() => {
    register('showAnswerKey')
    register('autoEnrollMode')
    register('autoEnrollProfileIds')
    register('notifyOnCompleteEnabled')
    register('notifyOnCompleteDate')
    register('accessType')
    register('loginMode')
  }, [register])

  const showAnswerKey = watch('showAnswerKey')
  const autoEnrollMode = watch('autoEnrollMode')
  const autoEnrollProfileIds = watch('autoEnrollProfileIds')
  const notifyOnCompleteEnabled = watch('notifyOnCompleteEnabled')
  const notifyOnCompleteDate = watch('notifyOnCompleteDate')
  const accessType = watch('accessType')
  const loginMode = watch('loginMode')

  useEffect(() => {
    if (!courseCompanyConfig?.id) return

    reset({
      showAnswerKey: courseCompanyConfig?.showAnswerKey ?? false,
      autoEnrollMode:
        courseCompanyConfig?.autoEnrollMode ?? AUTO_ENROLL_MODES.NONE,
      autoEnrollProfileIds: courseCompanyConfig?.autoEnrollProfileIds ?? [],
      notifyOnCompleteEnabled: !!courseCompanyConfig?.notifyOnCompleteDate,
      notifyOnCompleteDate: courseCompanyConfig?.notifyOnCompleteDate ?? '',
      accessType:
        courseCompanyConfig?.accessType ?? ACCESS_TYPES.PRIVATE_ACCESS,
      loginMode: courseCompanyConfig?.loginMode ?? LOGIN_MODES.TWO_FACTOR_EMAIL,
    })
  }, [courseCompanyConfig?.id])

  const { response: profilesResponse, isLoading: isProfilesLoading } = useFetch(
    service.dponet.profiles.get,
    {},
    [],
  )

  const profiles = profilesResponse?.data?.profiles ?? []

  const onSubmit = async (data) => {
    try {
      await service.dponet.trainingCourse.upsertCourseCompanyConfig({
        courseId,
        showAnswerKey: data.showAnswerKey,
        autoEnrollMode: data.autoEnrollMode,
        autoEnrollProfileIds:
          data.autoEnrollMode === AUTO_ENROLL_MODES.BY_PROFILE
            ? data.autoEnrollProfileIds
            : [],
        notifyOnCompleteDate: data.notifyOnCompleteEnabled
          ? data.notifyOnCompleteDate
          : null,
        accessType: data.accessType,
        loginMode: data.loginMode,
      })
      history.push(routes.trainingCourses.all)
      snackbar.open({
        message: 'Configurações salvas com sucesso.',
        variant: 'success',
      })
    } catch {
      snackbar.open({
        message: 'Erro ao salvar configurações.',
        variant: 'error',
      })
    }
  }

  if (isCourseCompanyConfigLoading) return <LoadingFeedback open />

  return (
    <Box component="form" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <AccessTypeCard
        isEdit={isEdit}
        accessType={accessType}
        loginMode={loginMode}
        setValue={setValue}
        courseId={courseId}
      />
      <Card className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Vinculação Automática por Perfil
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Define se novos usuários vinculados à empresa serão automaticamente
          matriculados neste curso.
        </Typography>

        <FormControl component="fieldset" className={classes.radioGroup}>
          <RadioGroup
            value={autoEnrollMode}
            onChange={(event) => setValue('autoEnrollMode', event.target.value)}
          >
            <Box className={classes.emphasisColor} px={2} mb={1}>
              <FormControlLabel
                disabled={!isEdit}
                value={AUTO_ENROLL_MODES.NONE}
                control={<Radio color="primary" />}
                label="Nenhum"
              />
            </Box>
            <Box className={classes.emphasisColor} px={2} mb={1}>
              <FormControlLabel
                disabled={!isEdit}
                value={AUTO_ENROLL_MODES.ALL}
                control={<Radio color="primary" />}
                label="Vincular todos"
              />
            </Box>
            <Box className={classes.emphasisColor} px={2} mb={1}>
              <FormControlLabel
                disabled={!isEdit}
                value={AUTO_ENROLL_MODES.BY_PROFILE}
                control={<Radio color="primary" />}
                label="Vincular por perfil específico"
              />
            </Box>
          </RadioGroup>
        </FormControl>
        {autoEnrollMode === AUTO_ENROLL_MODES.BY_PROFILE && (
          <Box className={classes.emphasisColor} px={2} pb={2}>
            <Autocomplete
              multiple
              options={profiles}
              loading={isProfilesLoading}
              getOptionLabel={(option) => option?.name ?? ''}
              getOptionSelected={(option, selected) =>
                option?.id === selected?.id
              }
              value={profiles.filter((profile) =>
                autoEnrollProfileIds?.includes(profile.id),
              )}
              onChange={(_, selected) =>
                setValue(
                  'autoEnrollProfileIds',
                  selected.map((profile) => profile.id),
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!errors.autoEnrollProfileIds}
                  helperText={
                    errors?.autoEnrollProfileIds
                      ? 'Selecione ao menos um perfil'
                      : ''
                  }
                  label="Perfis"
                  variant="outlined"
                  placeholder="Selecione os perfis"
                />
              )}
              className={classes.autocomplete}
              noOptionsText="Nenhum perfil encontrado"
            />
          </Box>
        )}
      </Card>

      <Card className={classes.section}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Respostas das Avaliações
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Visualizar a resposta correta e a explicação após responder
        </Typography>
        <Box className={classes.emphasisColor} p={2}>
          <Box className={classes.inlineBox}>
            <Box>
              <Typography variant="body2">
                Exibir a resposta e explicação
              </Typography>
              <Typography variant="body2">
                Permitir que o usuário visualize a resposta correta e a
                explicação após responder
              </Typography>
            </Box>
            <FormControlLabel
              className={classes.switchLabel}
              control={
                <Switch
                  disabled={!isEdit}
                  checked={showAnswerKey}
                  onChange={(event) =>
                    setValue('showAnswerKey', event.target.checked)
                  }
                  color="primary"
                />
              }
            />
          </Box>
        </Box>
      </Card>
      <CronNotificationsCard
        isEdit={isEdit}
        notifyOnCompleteEnabled={notifyOnCompleteEnabled}
        notifyOnCompleteDate={notifyOnCompleteDate}
        errors={errors}
        setValue={setValue}
        courseId={courseId}
      />
    </Box>
  )
}

export default StepSettings
